import { useCallback, useMemo, useState } from 'react'
import { useMutation, useSuspenseQuery } from '@apollo/client/react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'
import { GoodieFile, isNewFile, useUploadFile } from '@/features/files'
import { showToast, ToastType } from '@/features/toast'
import { formToGraphql, FormTranslations } from '@/features/translations'

export type FormData = {
  colors: {
    appBackground?: string
    appForeground?: string
    banner?: string
    btnRegular?: string
    error?: string
    info?: string
    navBackground?: string
    navForeground?: string
    primary: string
    txtLight?: string
    txtPlaceholder?: string
    txtRegular?: string
  }
  messageMorning: string
  messageNoon: string
  messageAfternoon: string
  messageEvening: string
  messageBirthday: string
  dayImage: GoodieFile
  nightImage: GoodieFile
  fontRegular: GoodieFile
  fontMedium: GoodieFile
  logo: GoodieFile
  map: GoodieFile
  currency: GraphQL.Currency
  timezone: string
  phoneNumber: string
  imprintUrl: string
  defaultLocale: GraphQL.AppLocale
  supportedLocales: GraphQL.AppLocale[]
  translations: FormTranslations
}

export const TRANSLATABLE_FIELDS: (keyof FormData)[] = [
  'messageMorning',
  'messageNoon',
  'messageAfternoon',
  'messageEvening',
  'messageBirthday'
]

type BradingWithFragments = GraphQL.BrandingFragmentFragment & {
  logo: GraphQL.FileFragmentFragment
  map?: GraphQL.FileFragmentFragment
  dayImage?: GraphQL.FileFragmentFragment
  nightImage?: GraphQL.FileFragmentFragment
  fontRegular?: GraphQL.FileFragmentFragment
  fontMedium?: GraphQL.FileFragmentFragment
}

export default function useBranding() {
  const { t } = useLingui()

  const { data } = useSuspenseQuery(AdminAPI.branding.GET_BRANDING)
  const [upload] = useUploadFile()
  const [updateBranding] = useMutation(AdminAPI.branding.UPDATE_BRANDING)
  const [loading, setLoading] = useState(false)

  const branding = useMemo(() => {
    const branding = {
      ...getFragment(SharedAPI.branding.BRANDING_FRAGMENT, data.getBranding)
    }

    branding.logo = getFragment(SharedAPI.file.FILE_FRAGMENT, branding.logo)

    branding.map = getFragment(SharedAPI.file.FILE_FRAGMENT, branding.map)

    branding.dayImage = getFragment(
      SharedAPI.file.FILE_FRAGMENT,
      branding.dayImage
    )

    branding.nightImage = getFragment(
      SharedAPI.file.FILE_FRAGMENT,
      branding.nightImage
    )

    branding.fontRegular = getFragment(
      SharedAPI.file.FILE_FRAGMENT,
      branding.fontRegular
    )

    branding.fontMedium = getFragment(
      SharedAPI.file.FILE_FRAGMENT,
      branding.fontMedium
    )

    return branding as BradingWithFragments
  }, [data])

  const { handlePromiseCatch, errors } = Form.useErrorHelpers<FormData>({})

  const fileToPromiseEntry = useCallback(
    (key: string, file?: GoodieFile | null) => {
      if (!file) return Promise.resolve([key, null])
      if (isNewFile(file)) return upload(file).then(({ id }) => [key, id])
      return Promise.resolve([key, file.id])
    },
    [upload]
  )

  const update = useCallback(
    async (data: FormData) => {
      const {
        logo,
        map,
        dayImage,
        nightImage,
        fontMedium,
        fontRegular,
        ...rest
      } = data

      setLoading(true)

      const fileUploads = Promise.all([
        fileToPromiseEntry('logoId', logo),
        fileToPromiseEntry('mapId', map),
        fileToPromiseEntry('dayImageId', dayImage),
        fileToPromiseEntry('nightImageId', nightImage),
        fileToPromiseEntry('fontMediumId', fontMedium),
        fileToPromiseEntry('fontRegularId', fontRegular)
      ])

      return fileUploads
        .then(entries => {
          const input = {
            ...rest,
            ...Object.fromEntries(entries),
            translations: formToGraphql(rest.translations)
          }

          return updateBranding({ variables: { input } })
        })
        .then(({ data, error }) => {
          if (error) return Promise.reject(error)
          if (!data) return Promise.reject('unknown')

          showToast({
            type: ToastType.SUCCESS,
            id: 'success-branding-update',
            message: t({
              comment:
                'Success toaster message when updating the branding worked',
              message: 'Successfully updated your branding.'
            })
          })
        })
        .catch(error =>
          handlePromiseCatch(error, {
            unknownString: t({
              comment:
                'Generic error message that we show when something unexpected happens.',
              message:
                'Something went wrong, please try again, if this keeps happening please write our support.'
            })
          })
        )
        .finally(() => setLoading(false))
    },
    [fileToPromiseEntry, updateBranding, handlePromiseCatch, t]
  )

  return {
    branding,
    errors,
    update,
    loading
  }
}
